import type { ComponentProps, FC } from "react";

import { Label, RadioGroup, RadioGroupItem } from "../../components";
import { cn } from "../../utils";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

export interface FieldRadioGroupOption {
  label: string;
  value: string;
  itemProps?: ComponentProps<typeof RadioGroupItem>;
  description?: string;
}

export type FieldRadioGroupProps = FieldBaseProps & {
  options: readonly FieldRadioGroupOption[];
  disabled?: boolean;
  onChange?: ComponentProps<typeof RadioGroup>["onValueChange"];
  onBlur?: ComponentProps<typeof RadioGroupItem>["onBlur"];
  inputProps?: ComponentProps<typeof RadioGroup>;
};

export const FieldRadioGroup: FC<FieldRadioGroupProps> = ({
  options,
  disabled,
  onChange,
  onBlur,
  inputProps,
  ...props
}) => {
  const { field, hasError } = useField({ name: props.name, disabled, inputProps });

  const handleChange: FieldRadioGroupProps["onChange"] = (value) => {
    field.onChange(value);
    onChange?.(value);
  };

  const handleBlur: FieldRadioGroupProps["onBlur"] = (event) => {
    field.onBlur();
    onBlur?.(event);
  };

  return (
    <FieldBase {...props}>
      <RadioGroup
        ref={field.ref}
        name={field.name}
        value={field.value}
        onValueChange={handleChange}
        disabled={field.disabled}
        {...inputProps}
        className={cn("my-1", inputProps?.className)}
      >
        {options.length > 0 && (
          <>
            {options.map((option) => {
              const isDisabled = field.disabled || option.itemProps?.disabled;

              return (
                <Label
                  key={option.value}
                  className="flex items-start gap-2 text-sm font-normal group-data-[disabled]/form-field:cursor-not-allowed group-data-[disabled]/form-field:opacity-70"
                >
                  <RadioGroupItem
                    value={option.value}
                    onBlur={handleBlur}
                    {...option.itemProps}
                    disabled={isDisabled}
                    className={cn(
                      "flex-none mt-0.5",
                      { "border-destructive": hasError && !isDisabled },
                      option.itemProps?.className
                    )}
                  />
                  <div className="flex flex-col gap-1">
                    <div className={cn({ "font-medium": option.description })}>{option.label}</div>
                    {option.description && <div className="text-muted-foreground">{option.description}</div>}
                  </div>
                </Label>
              );
            })}
          </>
        )}
      </RadioGroup>
    </FieldBase>
  );
};
